import '@/services/devOnlyConsole'
import AuthorizationDirectives from '@/plugins/AuthorizationDirectives'
import ClickOutsideDirective from '@/plugins/ClickOutsideDirective'
import FontAwesome from '@/plugins/FontAwesome'
import PageScrollDirective from '@/plugins/PageScrollDirective'
import router from '@/router'
import store from '@/store'
import { createApp } from 'vue'
import App from './App.vue'
// import Vue3Pagination from '@/plugins/Vue3Pagination'
import VeeValidatePlugin from '@/plugins/VeeValidatePlugin'
import vuetify from '@/plugins/vuetify'
import { initializeOrganizationBranding } from '@/services/organizationBranding'
import { createHead } from '@vueuse/head'
import { QrcodeCapture, QrcodeDropZone, QrcodeStream } from 'vue-qrcode-reader'

// Import App components globally - more reliable than require.context in modern builds
import AppAvatarImg from './components/AppAvatarImg.vue'
import AppCamera from './components/AppCamera.vue'
import AppDate from './components/AppDate.vue'
import AppFormField from './components/AppFormField.vue'
import AppHead from './components/AppHead.vue'
import AppInfiniteScroll from './components/AppInfiniteScroll.vue'
import AppMultiSelectField from './components/AppMultiSelectField.vue'
import AppNotifications from './components/AppNotifications.vue'
import AppSpinner from './components/AppSpinner.vue'
import OrganizationLogo from './components/OrganizationLogo.vue'

// Define the feature flag
window.__VUE_PROD_HYDRATION_MISMATCH_DETAILS__ = false

// Global modal scroll management helpers
// Maintain a stack so multiple modals can open without losing the original scroll state
window.__modalScrollStack = []
window.__modalScrollPos = 0
window.disableBodyScroll = function () {
  try {
    if (window.__modalScrollStack.length === 0) {
      window.__modalScrollPos = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.top = `-${window.__modalScrollPos}px`
      document.body.style.width = '100%'
    }
    window.__modalScrollStack.push(true)
  } catch (e) {
    // ignore
  }
}

window.enableBodyScroll = function () {
  try {
    window.__modalScrollStack.pop()
    if (window.__modalScrollStack.length === 0) {
      document.body.style.overflow = ''
      document.body.style.position = ''
      const top = document.body.style.top
      document.body.style.top = ''
      document.body.style.width = ''
      const scrollY = Math.abs(parseInt(top || '0')) || window.__modalScrollPos || 0
      window.scrollTo(0, scrollY)
    }
  } catch (e) {
    // ignore
  }
}

// Global error handler for unhandled API errors
const handleGlobalError = (error, instance, info) => {
  console.error('[GLOBAL ERROR HANDLER]', {
    error: error.message,
    component: instance?.$options.name || 'Unknown',
    info,
    timestamp: new Date().toISOString()
  })

  // Don't spam console with repetitive API errors
  if (error.message && error.message.includes('API Error: 500')) {
    // Log once and suppress repeated 500 errors for 5 seconds
    const errorKey = `api_500_${error.apiPath || 'unknown'}`
    const now = Date.now()
    const lastLogged = window._lastApiErrorLog?.[errorKey] || 0

    if (now - lastLogged > 5000) {
      window._lastApiErrorLog = window._lastApiErrorLog || {}
      window._lastApiErrorLog[errorKey] = now

      console.warn(`[API 500 ERROR] ${error.apiPath || 'unknown endpoint'} - suppressing similar errors for 5s`)
    }
  }
}

const forumApp = createApp(App)

// Configure global error handling
forumApp.config.errorHandler = handleGlobalError
forumApp.use(router)
forumApp.use(store)
forumApp.use(FontAwesome)
forumApp.use(ClickOutsideDirective)
forumApp.use(PageScrollDirective)
forumApp.use(AuthorizationDirectives)
// forumApp.use(Vue3Pagination)
forumApp.use(VeeValidatePlugin)
forumApp.use(createHead())

forumApp.use(vuetify)

forumApp.component('QrcodeStream', QrcodeStream)
forumApp.component('QrcodeDropZone', QrcodeDropZone)
forumApp.component('QrcodeCapture', QrcodeCapture)

// Register global components
forumApp.component('AppFormField', AppFormField)
forumApp.component('AppCamera', AppCamera)
forumApp.component('AppDate', AppDate)
forumApp.component('AppAvatarImg', AppAvatarImg)
forumApp.component('AppInfiniteScroll', AppInfiniteScroll)
forumApp.component('AppMultiSelectField', AppMultiSelectField)
forumApp.component('AppNotifications', AppNotifications)
forumApp.component('AppSpinner', AppSpinner)
forumApp.component('AppHead', AppHead)
forumApp.component('OrganizationLogo', OrganizationLogo)

// Tenant branding is loaded before mount so the first rendered frame already
// uses the organization theme. Without this explicit build-time variable no
// tenant request is made and the existing design is used as-is.
const organizationId = process.env.VUE_APP_ORGANIZATION_ID

initializeOrganizationBranding({ organizationId, vuetify })
  .finally(() => forumApp.mount('#app'))
